/**
 * MANAGEMENT (14) / Support data layer — the chat + triage hooks the Support SCREEN uses, ADDITIVE to
 * the shell's api.ts (which already ships `useSupportTickets` / `useSupportUnread` + `managementKeys` +
 * `isManagementAbsent`). This file adds the per-ticket detail/thread reads + the write mutations, reusing
 * the shell's key factory verbatim so a write invalidates the exact list/badge keys the shell owns.
 *
 * NEAR-REAL-TIME CHAT — the seam the task asks for. The reads accept a `live` flag that turns on TanStack
 * Query's `refetchInterval`: the open thread + the inbox + the unread badge poll on a short cadence so a
 * lab reply lands in the operator's chat within a few seconds with NO websocket. Polling is OFF (no
 * interval) unless the screen is gated-on AND online, so a backgrounded/forbidden screen never polls.
 *
 * Endpoints (all on the `platformRoute` chain, capability `management.support.manage`; verified against
 * server/src/modules/management/support.routes.js):
 *   GET   /management/support/tickets/:id            getTicket   → SupportTicketDetail (+ embedded thread)
 *   GET   /management/support/tickets/:id/messages   listMessages → SupportMessagePage
 *   POST  /management/support/tickets/:id/messages   postMessage  → SupportMessage
 *   PATCH /management/support/tickets/:id            triageTicket → SupportTicketRow (version-guarded)
 *   POST  /management/support/tickets/:id/read       markRead     → MarkReadResult
 */
import { api, useApiMutation, useApiQuery } from "@/api";
import { managementKeys } from "./api";
import type {
  MarkReadResult,
  MarkReadVars,
  PostMessageVars,
  SupportMessage,
  SupportMessagePage,
  SupportTicketDetail,
  TriageTicketResult,
  TriageTicketVars,
} from "./support.types";

/** The poll cadence for the live chat surfaces (ms). Config-driven (L-2) — not a magic literal inline. */
export const SUPPORT_LIVE_POLL_MS = 5_000;

/** The per-ticket key factory — extends `managementKeys` (the shell's) without re-rooting the namespace. */
export const supportKeys = {
  ticket: (id: string) => [...managementKeys.root, "support", "ticket", id] as const,
  messages: (id: string) => [...managementKeys.root, "support", "ticket", id, "messages"] as const,
};

/**
 * GET /management/support/tickets/:id — the ticket + its embedded thread. When `live` is set the thread
 * polls every {@link SUPPORT_LIVE_POLL_MS} so a lab reply appears in the open chat near-real-time.
 */
export function useSupportTicket(id: string | null, enabled: boolean, live = true) {
  return useApiQuery<SupportTicketDetail>(
    supportKeys.ticket(id ?? "—"),
    `/management/support/tickets/${id}`,
    {
      enabled: enabled && !!id,
      staleTime: 0,
      ...(live && enabled && !!id ? { refetchInterval: SUPPORT_LIVE_POLL_MS } : {}),
    },
  );
}

/**
 * GET /management/support/tickets/:id/messages — the paginated thread (used when the embedded thread is
 * not enough, e.g. a long history). Same live-poll seam as {@link useSupportTicket}.
 */
export function useSupportMessages(id: string | null, enabled: boolean, live = false) {
  return useApiQuery<SupportMessagePage>(
    supportKeys.messages(id ?? "—"),
    `/management/support/tickets/${id}/messages`,
    {
      enabled: enabled && !!id,
      staleTime: 0,
      ...(live && enabled && !!id ? { refetchInterval: SUPPORT_LIVE_POLL_MS } : {}),
    },
  );
}

/**
 * POST a chat message into a ticket thread (authored `admin` server-side). On success invalidate the
 * ticket detail + the inbox list (a reply nudges `updated_at`) + the unread badge.
 */
export function usePostSupportMessage() {
  return useApiMutation<SupportMessage, PostMessageVars>(
    ({ ticketId, contenu }) =>
      api.post(`/management/support/tickets/${ticketId}/messages`, { contenu }),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * PATCH triage (status/priority/category/assignee) — version-guarded optimistic concurrency. A stale
 * version returns a typed 409 (CONFLICT); the screen surfaces "reload then retry". Invalidate the whole
 * management root so the inbox counts + badges re-derive.
 */
export function useTriageTicket() {
  return useApiMutation<TriageTicketResult, TriageTicketVars>(
    ({ ticketId, ...patch }) => api.patch(`/management/support/tickets/${ticketId}`, patch),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * POST mark-read — clear the labo→admin unread on a ticket (idempotent). Invalidate the root so the
 * per-row unread badges + the cross-lab unread KPI drop immediately.
 */
export function useMarkSupportRead() {
  return useApiMutation<MarkReadResult, MarkReadVars>(
    ({ ticketId }) => api.post(`/management/support/tickets/${ticketId}/read`, {}),
    { invalidate: [managementKeys.root] },
  );
}
